// ABOUTME: Loading placeholder of a node-info widget, built from the same rows as the loaded fields.
// ABOUTME: Follows the widget config (icons, labels, separators, dense, file cards, image style) and each field's kind.

import { Box, Divider, Stack, Typography } from '@ds/coolshop';
import { CSSProperties } from 'react';
import { useIntl } from 'react-intl';

import { FILE_CARD_GAP } from './FileCardList';
import { NodeInfoRow } from './NodeInfoRow';
import { SkeletonShape } from './WidgetSkeleton';

/**
 * What a field renders once loaded: text lines (a system field such as `lastUpdate` has two: who
 * and when), an image, or a file card.
 */
export type NodeInfoFieldSkeleton =
    | { key: string; kind: 'lines'; lines: number; icon: boolean }
    | { key: string; kind: 'image'; icon: boolean }
    | { key: string; kind: 'fileCard' };

interface NodeInfoFieldsSkeletonProps {
    fields: NodeInfoFieldSkeleton[];
    showLabel: boolean;
    showSeparator: boolean;
    dense: boolean;
    fileCards: boolean;
    // The image style of the widget, which sizes its images.
    imageStyle: CSSProperties;
}

// The avatar of a field row and the height of a file card (NodeInfoWidget, FileCardList).
const FIELD_ICON_SIZE = 30;
const FILE_CARD_HEIGHT = 72;
// The default miniature of a node-info image (DEFAULT_IMAGE_STYLE of NodeInfoWidget).
const DEFAULT_IMAGE_MAX_WIDTH = 120;
const DEFAULT_IMAGE_MAX_HEIGHT = 64;

export function NodeInfoFieldsSkeleton({
    fields,
    showLabel,
    showSeparator,
    dense,
    fileCards,
    imageStyle,
}: NodeInfoFieldsSkeletonProps) {
    const { formatMessage } = useIntl();
    // A placeholder inside a Typography takes that variant's line box: the label and value rows
    // are exactly as tall as the loaded caption and subtitle2 lines.
    const label = <SkeletonShape variant="text" width="35%" />;

    return (
        <Stack
            role="status"
            aria-busy
            aria-live="polite"
            aria-label={formatMessage({ id: 'custom-pages.loading', defaultMessage: 'Loading…' })}
            gap={fileCards ? FILE_CARD_GAP : 0}
            divider={showSeparator ? <Divider /> : undefined}
        >
            {fields.map(field => {
                if (field.kind === 'fileCard') {
                    return (
                        <SkeletonShape
                            key={field.key}
                            variant="rounded"
                            width="100%"
                            height={FILE_CARD_HEIGHT}
                        />
                    );
                }
                const icon = field.icon ? (
                    <SkeletonShape
                        variant="circular"
                        width={FIELD_ICON_SIZE}
                        height={FIELD_ICON_SIZE}
                    />
                ) : undefined;
                return (
                    <NodeInfoRow
                        key={field.key}
                        label={label}
                        showLabel={showLabel}
                        dense={dense}
                        icon={icon}
                    >
                        {field.kind === 'image' ? (
                            // The image box of the style: its height, else the height it is capped at.
                            <Box
                                width={imageStyle.width ?? '100%'}
                                maxWidth={imageStyle.maxWidth ?? DEFAULT_IMAGE_MAX_WIDTH}
                                height={
                                    imageStyle.height ??
                                    imageStyle.maxHeight ??
                                    DEFAULT_IMAGE_MAX_HEIGHT
                                }
                            >
                                <SkeletonShape variant="rounded" width="100%" height="100%" />
                            </Box>
                        ) : (
                            Array.from({ length: field.lines }, (_, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Typography key={index} variant="subtitle2">
                                    <SkeletonShape variant="text" width="60%" />
                                </Typography>
                            ))
                        )}
                    </NodeInfoRow>
                );
            })}
        </Stack>
    );
}
